import React, { JSX, ReactNode } from 'react';
import styled, { css } from 'styled-components';

type TextColor = 'title' | 'subtitle' | 'description' | 'primary' | 'white';
type FontWeight = 300 | 400 | 500 | 600 | 700 | 800;

interface TypographyProps {
  $color?: TextColor;
  $weight?: FontWeight;
  $mb?: number;
  $fontsize?: string;
  $lineheight?: string;
  $width?: string;
  className?: string;
  children?: ReactNode;
  $fontstyle?: string;
}

interface HeadingProps extends TypographyProps {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  id?: string;
}

// Common Styles ==========
const CommonStyles = css<TypographyProps>`
  margin: 0;
  font-size: ${({ $fontsize }) => $fontsize || 'inherit'};
  line-height: ${({ $lineheight }) => $lineheight || 'inherit'};
  font-weight: ${({ $weight }) => ($weight ? WeightMap[$weight] : 400)};
  color: ${({ $color }) => ($color ? ColorMap[$color] : 'inherit')};
  margin-bottom: ${({ $mb }) => ($mb !== undefined ? `${$mb}px` : '0')};
  width: ${({ $width }) => $width || 'inherit'};
  max-width: 100%;
  font-style: ${({ $fontstyle }) => $fontstyle || 'normal'};
`;

const ColorMap: Record<TextColor, string> = {
  title: 'var(--clr-title)',
  subtitle: 'var(--clr-subtitle)',
  description: 'var(--clr-general-description)',
  primary: 'var(--clr-general-primary)',
  white: 'var(--clr-white)',
};

const WeightMap: Record<FontWeight, number> = {
  300: 300,
  400: 400,
  500: 500,
  600: 600,
  700: 700,
  800: 800,
};

// Heading Component ==========
export const Heading = styled(
  React.forwardRef<HTMLElement, HeadingProps>(({ level = 1, children, ...props }, ref) => {
    const Tag = `h${level}` as keyof JSX.IntrinsicElements;
    return React.createElement(Tag, { ...props, ref }, children);
  }),
)<HeadingProps>`
  ${CommonStyles}
`;

// Paragraph Component ==========
export const Paragraph = styled.p<TypographyProps>`
  ${CommonStyles}
`;

// Text Component ==========
export const Text = styled.span<TypographyProps>`
  ${CommonStyles}
`;
